import { useState, useEffect, useRef, useMemo } from 'react'
import { useTheme } from '@mui/material/styles'
import {
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  TextField,
  ToggleButton,
  Typography
} from '@mui/material'
import * as Icons from '@mui/icons-material'
import { StaticMap } from '~/map/static-map'
import { usePolygonDraw } from './use-polygon-draw'
import { PolygonDrawLayer } from './polygon-draw-layer'
import { DistanceTooltip } from './distance-tooltip'
import { DistanceMeasurementLines } from './distance-measurement-lines'
import { AreaMeasurement } from './area-measurement'
import { DrawControls } from './draw-controls'
import { useCreateNewPlot } from './use-create-new-plot'
import { useCreateNewReference } from './use-create-new-reference'
import { useCropData } from '~/crop/crop-list/use-crop-data'
import { useOrgParams } from '~/common/use-org-params'
import { useAssignCrops } from './use-assign-crops'
import { useOrgContext } from '../../common/context/organization/org-context'
import { useFarmClimate } from '~/map/use-farm-climate'
import { TpChip } from '~/components/tp-chip'
import { useMultiCropSuccessionData } from '~/crop/crop-schedule/hooks/use-multi-crop-succession-data'
import { ClosingAwareModal } from '~/components/closing-aware-modal'
import { PlotHelpTooltip } from './plot-help-tooltip'
import { TpToggleButtonGroup } from '~/components'

const DEFAULT_REFERENCE_COLOR = '#80cbc4'

export const AddPlotModal = ({ open, onClose }) => {
  const { palette } = useTheme()
  const { farmId } = useOrgParams()
  const [mode, setMode] = useState('plot')
  const [name, setName] = useState(null)
  const [nameFieldBlurred, setNameFieldBlurred] = useState(false)
  const [cropIdsToAdd, setCropIdsToAdd] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showCartesian, setShowCartesian] = useState(false)
  const [selectedColor, setSelectedColor] = useState(DEFAULT_REFERENCE_COLOR)
  const [cursorScreenPos, setCursorScreenPos] = useState(null)
  const [lastPointScreenPos, setLastPointScreenPos] = useState(null)
  const [centroidScreenPos, setCentroidScreenPos] = useState(null)
  const [mapSize, setMapSize] = useState(0)
  const mapContainerRef = useRef(null)
  const nameFieldRef = useRef(null)

  const isReference = mode === 'reference'

  const {
    points,
    cursorPosition,
    isComplete,
    addPoint,
    undoPoint,
    closePolygon,
    resetDrawing,
    updateCursor,
  } = usePolygonDraw()

  const { activeOrgs: [ farmOrg ] } = useOrgContext()
  const { createNewPlot } = useCreateNewPlot({ name, points })
  const { createNewReference } = useCreateNewReference({ name, points, colour: selectedColor })
  const { crops } = useCropData()
  const unassignedCrops = useMemo(() =>
    crops.filter(crop => crop.organization_id === farmId),
    [crops, farmId]
  )
  const { successionMap } = useMultiCropSuccessionData({ crops: unassignedCrops })

  const allCropIdsToAssign = useMemo(() => {
    const successionIds = cropIdsToAdd.flatMap(id =>
      (successionMap.get(id)?.children || []).map(c => c.id)
    )
    return [...cropIdsToAdd, ...successionIds]
  }, [cropIdsToAdd, successionMap])

  const { assignCrops } = useAssignCrops({ cropIds: allCropIdsToAssign })
  const { climate } = useFarmClimate()

  const canSubmit = isComplete && points.length >= 3 && name

  const _onClose = () => {
    resetDrawing()
    setMode('plot')
    setName(null)
    setNameFieldBlurred(null)
    setCropIdsToAdd([])
    setSelectedColor(DEFAULT_REFERENCE_COLOR)
    setCursorScreenPos(null)
    setLastPointScreenPos(null)
    setMapSize(0)
    setIsSubmitting(false)
    onClose && onClose()
  }

  const onModeChange = (_, newMode) => {
    if (!newMode || newMode === mode) return
    resetDrawing()
    setName(null)
    setNameFieldBlurred(false)
    setCropIdsToAdd([])
    setMode(newMode)
  }

  const onSubmit = async () => {
    if (!canSubmit) return
    setIsSubmitting(true)
    try {
      if (isReference) {
        await createNewReference()
      } else {
        const newPlotId = await createNewPlot()
        if (cropIdsToAdd.length > 0 && newPlotId) {
          assignCrops({ orgId: newPlotId })
        }
      }
    } finally {
      _onClose()
    }
  }

  const lastPoint = points.length > 0 ? points[points.length - 1] : null

  const centroid = useMemo(() => {
    if (!isComplete || !points || points.length < 3) return null

    const sum = points.reduce(
      (acc, point) => ({
        x: acc.x + point.x,
        y: acc.y + point.y,
      }),
      { x: 0, y: 0 }
    )

    return {
      x: sum.x / points.length,
      y: sum.y / points.length,
    }
  }, [points, isComplete])

  useEffect(() => {
    if (!open) return

    const container = mapContainerRef.current
    if (!container) return

    const width = container.clientWidth
    if (width > 0) {
      setMapSize(width)
    }
  }, [open])

  useEffect(() => {
    const container = mapContainerRef.current
    if (!container) return

    let isMounted = true

    const resizeObserver = new ResizeObserver((entries) => {
      if (!isMounted) return

      for (const entry of entries) {
        const width = entry.contentRect.width
        if (width > 0) {
          setMapSize(width)
        }
      }
    })

    resizeObserver.observe(container)

    return () => {
      isMounted = false
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (nameFieldRef.current) nameFieldRef.current.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <ClosingAwareModal
      open={open}
      onClose={_onClose}
      loading={isSubmitting}
    >
      <Box
        component="form"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90vw', md: '80vw', lg: '70vw' },
          bgcolor: palette.background.paper,
          borderRadius: '0.5rem',
          boxShadow: 24,
          p: '1rem 2rem',
          userSelect: 'none',
        }}
      >
        {/* Mode toggle */}
        <Box sx={{ mb: 1 }}>
          <TpToggleButtonGroup
            exclusive
            value={mode}
            onChange={onModeChange}
          >
            <ToggleButton value="plot">Plot</ToggleButton>
            <ToggleButton value="reference">Reference</ToggleButton>
          </TpToggleButtonGroup>
        </Box>

        <Typography id="modal-modal-title" variant="h4">
          {isReference ? 'Add Reference' : 'Add Plot'}
        </Typography>

        <Typography id="modal-modal-description" variant="subtitle">
          {isReference
            ? <>
                Draw a polygon to mark a reference feature on your farm map (e.g. a building, path, hedge, or forest).
                Reference shapes are purely visual and do not affect crop navigation.
                <span style={{ color: palette.primary.light }}> You can edit or remove these at any time.</span>
              </>
            : <>
                Create a polygon on your farm's static map to reprsent single or multiple beds.
                Plots create new location filters and navigation links for organizing your crops across your farm.
                <span style={{ color: palette.primary.light }}> You can edit or remove these at any time.</span>
              </>
          }
        </Typography>

        {!isReference && <PlotHelpTooltip />}

        <Box sx={{ width: '100%', mt: 2 }}>
          <Grid direction={{ xs: 'column', sm: 'row' }} container spacing={2} rowSpacing={1}>
            <Grid size={{ xs: 12, md: 4 }} alignContent='center'>
              <TextField
                size='small'
                inputRef={nameFieldRef}
                id="plot-name-field"
                label={isReference ? 'Reference Name' : 'Plot Name'}
                required
                error={!name && nameFieldBlurred}
                fullWidth
                value={name}
                sx={{ userSelect: 'text' }}
                slotProps={{ htmlInput: {style: { resize: 'none' }} }}
                onChange={(event) => setName(event.target.value || '')}
                onBlur={() => setNameFieldBlurred(true)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 8 }} alignContent='center'>
              <Typography id="modal-name-helper-text" variant="body" sx={{ textAlign: 'left' }}>
                {isComplete
                  ? <span style={{ color: palette.success.main }}>
                      {isReference ? 'Reference boundary complete! Click Add to save.' : 'Plot boundary complete! Click Add to save.'}
                    </span>
                  : <span style={{ color: palette.info.main }}>
                      Click on the map to draw your {isReference ? 'reference' : 'plot'} boundary. Click the first point to close.
                    </span>
                }
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: '0.5rem' }} variant='fullWidth' />

        <Box
          ref={mapContainerRef}
          sx={{
            width: { xs: '70vw', sm: '60vw', md: '50vw', lg: '40vw' },
            aspectRatio: '1/1',
            position: 'relative',
            margin: '0 auto',
          }}
        >
          <StaticMap
            key={mapSize}
            width={mapSize}
            height={mapSize}
            showPlotOrganizationPolygons
            fullImage
            clampToBounds
          >
            <PolygonDrawLayer
              points={points}
              isComplete={isComplete}
              addPoint={addPoint}
              closePolygon={closePolygon}
              updateCursor={updateCursor}
              onCursorScreenPos={setCursorScreenPos}
              onLastPointScreenPos={setLastPointScreenPos}
              centroid={centroid}
              onCentroidScreenPos={setCentroidScreenPos}
            />
          </StaticMap>

          {!isComplete && (
            <DistanceTooltip
              lastPoint={lastPoint}
              cursorPosition={cursorPosition}
              cursorScreenPos={cursorScreenPos}
              climateBounds={climate?.bounds}
            />
          )}

          {!isComplete && showCartesian && (
            <DistanceMeasurementLines
              lastPoint={lastPoint}
              cursorPosition={cursorPosition}
              lastPointScreenPos={lastPointScreenPos}
              cursorScreenPos={cursorScreenPos}
              climateBounds={climate?.bounds}
            />
          )}

          {isComplete && (
            <AreaMeasurement
              points={points}
              isComplete={isComplete}
              climateBounds={climate?.bounds}
              centroidScreenPos={centroidScreenPos}
            />
          )}

          <DrawControls
            points={points}
            onUndo={undoPoint}
            onClear={resetDrawing}
            showCartesian={showCartesian}
            onToggleCartesian={() => setShowCartesian(prev => !prev)}
            referenceColor={isReference ? selectedColor : undefined}
            onColorChange={isReference ? setSelectedColor : undefined}
          />
        </Box>

        {/* CROP CHIPS — plot mode only */}
        {!isReference && unassignedCrops.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: '0.5rem' }}>
            <Box>
              <Typography variant='h6' sx={{ fontWeight: 400 }}>
                Assign crops from <strong><span style={{ color: palette.error.main }}>{farmOrg?.name || ''}</span></strong>
                {' '}to <strong><span style={{ color: palette.secondary.main }}>{name ?? 'this new plot'}</span></strong>
              </Typography>
              <Divider variant='fullWidth' />
            </Box>

            <Box justifyContent={{ xs: 'center', sm: 'flex-start' }} sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                mb: 1,
              }}>
                {unassignedCrops.map((crop) => {
                    const isSelected = cropIdsToAdd.includes(crop.id)
                    return (
                      <TpChip
                        isSelected={isSelected}
                        key={crop.id}
                        label={
                          <Stack direction='row' alignItems='center' gap={0.75}>
                            {crop.name} {crop.count}
                            {successionMap.get(crop.id) && (
                              <Stack direction='row' alignItems='center' gap={0.25}>
                                <Icons.Repeat sx={{ fontSize: 12, flexShrink: 0 }} />
                                {successionMap.get(crop.id).count}
                              </Stack>
                            )}
                          </Stack>
                        }
                        onClick={() => {
                          if (isSelected) {
                            setCropIdsToAdd(prev => prev.filter(id => id !== crop.id))
                            return
                          }
                          setCropIdsToAdd(prev => [...prev, crop.id])
                        }}
                      />
                    )
                  })
                }
              </Box>
          </Box>
        )}

        <Box
          sx={{
            mt: '2rem',
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end'
          }}
        >
          <Button variant='outlined' size='large' onClick={_onClose}>
            Cancel
          </Button>
          <Button size='large' onClick={onSubmit} disabled={!canSubmit}>
            {isReference
              ? 'Add'
              : `Add${cropIdsToAdd.length > 0 ? ` & assign ${cropIdsToAdd.length} crop${cropIdsToAdd.length > 1 ? 's' : ''}` : ''}`
            }
          </Button>
        </Box>
      </Box>
    </ClosingAwareModal>
  )
}
